import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Zap, Leaf, Globe, ArrowRight, ExternalLink, Users, Handshake, Cloud, Film } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';

export default function Partners() {
  const { t } = useLanguage();
  useSEO({
    title: 'Partners',
    description:
      'Meet the partners of GRAWEO Charitable Foundation — organisations and companies supporting our humanitarian and business recovery mission.',
  });

  const partners = [
    {
      id: 'u-cloud24',
      name: 'U-Cloud24',
      website: 'https://u-cloud24.com',
      logo: '☁️',
      shortDesc: t('partner_ucloud_short'),
      fullDesc: t('partner_ucloud_full'),
      role: t('partner_ucloud_role'),
      expertise: [
        t('partner_ucloud_expertise_cloud'),
        t('partner_ucloud_expertise_ai'),
        t('partner_ucloud_expertise_security'),
        t('partner_ucloud_expertise_compliance'),
      ],
      areas: [
        t('partner_ucloud_area_cloud'),
        t('partner_ucloud_area_devops'),
        t('partner_ucloud_area_ai'),
        t('partner_ucloud_area_security'),
      ],
      roles: [
        t('partner_ucloud_role_1'),
        t('partner_ucloud_role_2'),
        t('partner_ucloud_role_3'),
        t('partner_ucloud_role_4'),
      ],
      icon: Cloud,
    },
    {
      id: 'craft-me',
      name: 'Craft-Me',
      website: 'https://craft-me.com',
      logo: '🎬',
      shortDesc: t('partner_craftme_short'),
      fullDesc: t('partner_craftme_full'),
      role: t('partner_craftme_role'),
      expertise: [
        t('partner_craftme_expertise_video'),
        t('partner_craftme_expertise_visual'),
        t('partner_craftme_expertise_campaigns'),
        t('partner_craftme_expertise_communication'),
      ],
      areas: [
        t('partner_craftme_area_video'),
        t('partner_craftme_area_content'),
        t('partner_craftme_area_social'),
        t('partner_craftme_area_creative'),
      ],
      roles: [
        t('partner_craftme_role_1'),
        t('partner_craftme_role_2'),
        t('partner_craftme_role_3'),
        t('partner_craftme_role_4'),
      ],
      icon: Film,
    },
    {
      id: 'daos',
      name: 'DAOS',
      website: 'https://daos.com.ua',
      logo: '💚',
      shortDesc: t('partner_daos_short'),
      fullDesc: t('partner_daos_full'),
      role: t('partner_daos_role'),
      expertise: [
        t('partner_daos_expertise_1'),
        t('partner_daos_expertise_2'),
        t('partner_daos_expertise_3'),
        t('partner_daos_expertise_4'),
      ],
      areas: [
        t('partner_daos_area_1'),
        t('partner_daos_area_2'),
        t('partner_daos_area_3'),
        t('partner_daos_area_4'),
      ],
      roles: [
        t('partner_daos_role_1'),
        t('partner_daos_role_2'),
        t('partner_daos_role_3'),
        t('partner_daos_role_4'),
      ],
      icon: Globe,
    },
  ];

  const benefits = [
    {
      icon: Handshake,
      title: t('partnership_benefit_1_title'),
      desc: t('partnership_benefit_1_desc'),
    },
    {
      icon: Zap,
      title: t('partnership_benefit_2_title'),
      desc: t('partnership_benefit_2_desc'),
    },
    {
      icon: Leaf,
      title: t('partnership_benefit_3_title'),
      desc: t('partnership_benefit_3_desc'),
    },
    {
      icon: Globe,
      title: t('partnership_benefit_4_title'),
      desc: t('partnership_benefit_4_desc'),
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
              {t('partners_title')}
            </h1>
            <p className="text-xl text-gray-100 leading-relaxed">
              {t('partners_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('partners_our_partners')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner) => {
              return (
                <Card
                  key={partner.id}
                  className="overflow-hidden hover:shadow-xl transition-shadow flex flex-col"
                >
                  <div className="p-8 flex flex-col h-full">
                    {/* Header */}
                    <div className="mb-6">
                      <div className="text-6xl mb-4">{partner.logo}</div>
                      <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-2">
                        {partner.name}
                      </h3>
                      <p className="text-md text-graweo-terra font-semibold mb-4">
                        {partner.role}
                      </p>
                    </div>

                    {/* Short Description */}
                    <p className="text-gray-600 leading-relaxed flex-grow mb-6">
                      {partner.shortDesc}
                    </p>

                    {/* Buttons */}
                    <div className="flex gap-3 mt-auto">
                      <Link to={`/partners/${partner.id}`} className="flex-1">
                        <Button variant="primary" className="w-full">
                          {t('learn_more')}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Call for Partners */}
          <Card className="mt-12 bg-gradient-to-br from-graweo-terra/5 to-graweo-tan/5 p-8 text-center">
            <Users className="w-16 h-16 text-graweo-terra mx-auto mb-4" />
            <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-3">
              {t('partners_join_title')}
            </h3>
            <p className="text-gray-700 max-w-2xl mx-auto mb-6">
              {t('partners_join_desc')}
            </p>
            <Link to="/contacts">
              <Button variant="primary">
                {t('partners_contact_us')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('partners_benefits_title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const BenefitIcon = benefit.icon;

              return (
                <Card key={index} className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-graweo-terra/10 rounded-full flex items-center justify-center">
                      <BenefitIcon className="w-8 h-8 text-graweo-terra" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold font-manrope text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Values */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('partners_values_title')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4].map((value) => (
              <Card key={value} className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t(`partners_value_${value}_title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`partners_value_${value}_desc`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            {t('partners_cta_title')}
          </h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            {t('partners_cta_text')}
          </p>
          <Link to="/contacts">
            <Button variant="secondary" size="lg">
              {t('partners_cta_button')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
